import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { leadInboxApi, type LeadInboxItem, type LeadInboxPage as LeadInboxData, type LeadInboxScope } from '../api/leadInbox'
import { leadsApi, type LeadSourceOption } from '../api/leads'
import { campaignsApi, type Campaign } from '../api/campaigns'
import {
  Button, Callout, Card, CardActions, CardHeader, Checkbox, DataTable, EmptyState, FilterBar, LoadingState,
  Page, PageHeader, Select, StatusPill,
} from '../components/ui'

// F-110 (REQ-065-001): "Nuovi lead". Chi lavora i lead vede in una sola schermata quelli appena arrivati, da quanto
// tempo aspettano e da quanto non vengono chiamati. Si aggiorna da sola ogni minuto. Sola lettura, nessun importo.
// I target di SLA sono provvisori (stessi del cruscotto del call center); una presa in carico non esiste.

const REFRESH_MS = 60_000

const SCOPES: { value: LeadInboxScope; label: string }[] = [
  { value: 'all', label: 'Tutti' },
  { value: 'unassigned', label: 'Non assegnati' },
  { value: 'assigned', label: 'Assegnati' },
]

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Nuovi' },
  { value: 'Contacted', label: 'Contattati' },
  { value: 'Qualified', label: 'Qualificati' },
  { value: 'all', label: 'Tutti gli stati' },
]

/** "3 minuti", "2h 18m", "3 giorni": durata leggibile da un numero di minuti. */
function formatMinutes(total: number): string {
  const m = Math.max(0, Math.floor(total))
  if (m < 1) return 'meno di un minuto'
  if (m < 60) return m === 1 ? '1 minuto' : `${m} minuti`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`
  const d = Math.floor(h / 24)
  return d === 1 ? '1 giorno' : `${d} giorni`
}

function locality(i: LeadInboxItem): string {
  return [i.city, i.province ? `(${i.province})` : null].filter(Boolean).join(' ') || '—'
}

export function LeadInboxPage() {
  const [scope, setScope] = useState<LeadInboxScope>('all')
  const [source, setSource] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [status, setStatus] = useState('')
  const [notContactedOnly, setNotContactedOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<LeadInboxData | null>(null)
  const [sources, setSources] = useState<LeadSourceOption[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const requestId = useRef(0)

  useEffect(() => {
    let alive = true
    leadsApi.sources().then(r => { if (alive) setSources(r) }).catch(() => { if (alive) setSources([]) })
    campaignsApi.list({ pageSize: 100 }).then(r => { if (alive) setCampaigns(r.items) }).catch(() => { if (alive) setCampaigns([]) })
    return () => { alive = false }
  }, [])

  const load = useCallback(async (background: boolean) => {
    const id = ++requestId.current
    if (!background) setLoading(true)
    setError(null)
    try {
      const r = await leadInboxApi.list({
        scope,
        source: source || undefined,
        campaignId: campaignId ? Number(campaignId) : undefined,
        notContactedOnly,
        status: status || undefined,
        page,
      })
      if (id !== requestId.current) return
      setData(r)
      setUpdatedAt(new Date())
    } catch (e) {
      if (id === requestId.current) setError(e instanceof Error ? e.message : 'Errore nel caricamento dei nuovi lead')
    } finally {
      if (id === requestId.current) setLoading(false)
    }
  }, [scope, source, campaignId, status, notContactedOnly, page])

  useEffect(() => {
    void load(false)
    const timer = window.setInterval(() => void load(true), REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [load])

  function changeFilter(apply: () => void) {
    apply()
    setPage(1)
  }

  const items = data?.items ?? []
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <Page>
      <PageHeader
        title="Nuovi lead"
        icon="mark_email_unread"
        subtitle={
          updatedAt ? (
            <span aria-live="polite">
              Aggiornato alle {updatedAt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : undefined
        }
        actions={
          <Button type="button" variant="outlined" onClick={() => void load(false)} disabled={loading}>
            Aggiorna
          </Button>
        }
      />

      <Callout tone="info" role="note">
        I lead appena arrivati, dal più vecchio: il tempo mostra da quanto il lead aspetta e da quanto non viene
        chiamato (senza tentativi conta dall&apos;arrivo). &quot;Fuori SLA&quot; è un lead nuovo mai chiamato oltre il
        target della sua fonte (target provvisori). La pagina si aggiorna da sola ogni minuto.
        {data?.slaTargetsProvisional && ' I target di SLA sono valori provvisori.'}
      </Callout>

      <FilterBar>
        <Select id="li-scope" label="Assegnazione" value={scope}
          onChange={e => changeFilter(() => setScope(e.target.value as LeadInboxScope))}>
          {SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <Select id="li-status" label="Stato" value={status}
          onChange={e => changeFilter(() => setStatus(e.target.value))}>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <Select id="li-source" label="Fonte" value={source}
          onChange={e => changeFilter(() => setSource(e.target.value))}>
          <option value="">Tutte</option>
          {sources.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
        </Select>
        <Select id="li-campaign" label="Campagna" value={campaignId}
          onChange={e => changeFilter(() => setCampaignId(e.target.value))}>
          <option value="">Tutte</option>
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Checkbox id="li-never" label="Solo mai contattati" checked={notContactedOnly}
          onChange={e => changeFilter(() => setNotContactedOnly(e.target.checked))} />
      </FilterBar>

      {error && (
        <Callout tone="error" role="alert" action={<Button type="button" variant="text" onClick={() => void load(false)}>Riprova</Button>}>
          {error}
        </Callout>
      )}

      <Card variant="outlined" padding="none" aria-busy={loading}>
        <CardHeader title="Lead" actions={data && <span className="md-typescale-body-small text-muted" aria-live="polite">{data.total} lead</span>} />
        {loading && !data ? (
          <LoadingState />
        ) : data && items.length === 0 ? (
          <EmptyState icon="inbox" title="Nessun lead con questi filtri." />
        ) : (
          <DataTable variant="plain" caption="Nuovi lead, dal più vecchio">
            <thead>
              <tr>
                <th scope="col">Lead</th>
                <th scope="col">Contatti</th>
                <th scope="col">Fonte</th>
                <th scope="col">Campagna</th>
                <th scope="col">Località</th>
                <th scope="col">Agente</th>
                <th scope="col">Stato</th>
                <th scope="col">Arrivato</th>
                <th scope="col">Ultimo contatto</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id}>
                  <td>
                    <Link to={`/leads/${i.id}`} className="text-decoration-none md-typescale-emphasized-body-medium">{i.name || `Lead #${i.id}`}</Link>
                    {i.sector && <div className="md-typescale-body-small text-muted">{i.sector}</div>}
                  </td>
                  <td>
                    {i.phone ? (i.phone.includes('*') ? <span>{i.phone}</span> : <a href={`tel:${i.phone}`} className="text-decoration-none">{i.phone}</a>) : <span className="md-typescale-body-small text-muted">—</span>}
                    {i.email && <div className="md-typescale-body-small"><a href={`mailto:${i.email}`} className="text-decoration-none">{i.email}</a></div>}
                  </td>
                  <td>{i.sourceLabel ?? '—'}</td>
                  <td>{i.campaign?.name ?? '—'}</td>
                  <td>{locality(i)}</td>
                  <td>{i.agent ? (i.agent.name ?? `#${i.agent.id}`) : <span className="md-typescale-body-small text-muted">Non assegnato</span>}</td>
                  <td>
                    <div>{i.operationalState?.label ?? i.status}</div>
                    {i.outOfSla && <StatusPill tone="error">Fuori SLA</StatusPill>}
                  </td>
                  <td className="text-nowrap">ricevuto {formatMinutes(i.minutesSinceArrival)} fa</td>
                  <td className="text-nowrap">
                    {i.neverContacted
                      ? <span className="text-danger">mai contattato</span>
                      : `non contattato da ${formatMinutes(i.minutesSinceLastContact)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
        {data && pages > 1 && (
          <CardActions align="end">
            <Button type="button" variant="outlined" size="sm" disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}>
              Precedente
            </Button>
            <span className="md-typescale-body-small text-muted">Pagina {page} di {pages}</span>
            <Button type="button" variant="outlined" size="sm" disabled={page >= pages || loading}
              onClick={() => setPage(p => p + 1)}>
              Successiva
            </Button>
          </CardActions>
        )}
      </Card>
    </Page>
  )
}
